<template>
  <div v-if="showPanel" class="scene-info-panel">
    <!-- 场景标题、氛围和背景故事 -->
    <div class="scene-header-row">
      <div class="scene-title-section">
        <div class="scene-title-row">
          <span class="scene-icon">{{ getSceneIcon() }}</span>
          <h3>{{ currentScene.name }}</h3>
          <div class="location-badge">{{ gameState.location }}</div>
          <div class="scene-atmosphere">
            <span class="atmosphere-indicator">
              <span class="pulse-dot"></span>
              灵气充裕
            </span>
            <span class="time-indicator">
              <span class="time-icon">🌙</span>
              深夜
            </span>
          </div>
        </div>
      </div>
      <div class="scene-background">
        <p class="background-text">{{ currentScene.background }}</p>
      </div>
    </div>

    <!-- 可用行动建议 -->
    <div class="available-actions panel-section">
      <div class="panel-header actions-header">
        <span class="panel-header-icon actions-icon">⚡</span>
        <span class="panel-header-title actions-title">建议行动</span>
      </div>
      <div class="action-grid">
        <div
          v-for="action in suggestedActions"
          :key="action.id"
          class="action-item"
          :class="{ 'action-disabled': !action.implemented }"
          @click="action.implemented && executeAction(action.command)"
        >
          <span class="action-icon">{{ action.icon }}</span>
          <div class="action-content">
            <div class="action-text">
              <div class="action-name">{{ action.name }}</div>
              <div class="action-desc">{{ action.description }}</div>
            </div>
            <div v-if="!action.implemented" class="coming-soon">即将开放</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 场景特色信息 -->
    <div class="scene-features panel-section">
      <div class="panel-header features-header">
        <span class="panel-header-icon features-icon">✨</span>
        <span class="panel-header-title features-title">{{ currentScene.name }}特色</span>
      </div>
      <div class="features-grid">
        <div v-for="feature in sceneFeatures" :key="feature.id" class="feature-item">
          <span class="feature-icon">{{ feature.icon }}</span>
          <span class="feature-text">{{ feature.text }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { explorationScenes, LOCATION_NAMES } from './exploration/exploration-data.js';

// 境界相关常量（从realms.js复制）
const REALM_NAMES = ['炼气', '筑基', '金丹', '元婴', '化神', '渡劫', '真仙'];
const SUBSTAGE_NAMES = ['1层', '2层', '3层', '4层', '5层', '6层', '7层', '8层', '9层', '10层'];
const PERIOD_NAMES = ['前期', '前期', '前期', '中期', '中期', '中期', '后期', '后期', '后期', '巅峰'];

export default {
  name: 'SceneInfoPanel',
  props: {
    gameState: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showPanel: true
    };
  },
  computed: {
    currentScene() {
      return explorationScenes[this.gameState.location] || explorationScenes[LOCATION_NAMES.UNKNOWN];
    },

    suggestedActions() {
      const location = this.gameState.location;
      const baseActions = [];

      // 已实现的命令列表
      const implementedCommands = [
        'refine_pill',
        'refine_treasure',
        'explore',
        'bag',
        'travel',
        'status',
        'help',
        'treasure_hall'
      ];

      // 根据位置提供不同的建议行动
      switch (location) {
        case LOCATION_NAMES.UNKNOWN:
          baseActions.push(
            { id: 'treasure_hall', name: '聚宝阁', description: '神秘宝阁，购买丹方秘籍', command: 'treasure_hall', icon: '🏪', implemented: true },
            { id: 'explore', name: '探索未知', description: '探寻未知之地的奥秘', command: 'explore', icon: '🔍', implemented: true }
          );
          break;
        case LOCATION_NAMES.CAVE:
          baseActions.push(
            { id: 'refine_pill', name: '炼制丹药', description: '炼丹铸药，增进修为', command: 'refine_pill', icon: '🧪', implemented: true },
            { id: 'refine_treasure', name: '炼制法器', description: '炼器铸宝，增强实力', command: 'refine_treasure', icon: '⚔️', implemented: true },
            // { id: 'practice', name: '练习技能', description: '提升战斗技巧', command: 'practice', icon: '⚔️' },
            { id: 'explore', name: '探索洞府', description: '寻找机缘与宝物', command: 'explore', icon: '🔍', implemented: true }
          );
          break;
        case LOCATION_NAMES.BACK_MOUNTAIN:
          baseActions.push(
            { id: 'sect_trial', name: '宗门试炼', description: '挑战试炼，提升境界', command: 'sect_trial', icon: '⚡', implemented: false },
            { id: 'sect_inheritance', name: '宗门传承', description: '学习秘籍，传承道统', command: 'sect_inheritance', icon: '📚', implemented: false },
            { id: 'explore', name: '探索后山', description: '猎取妖兽，采集资源', command: 'explore', icon: '🌲', implemented: true },
            // { id: 'forbidden', name: '进入禁地', description: '挑战高难度区域', command: 'forbidden', icon: '🚫' }
          );
          break;
        case LOCATION_NAMES.SECRET_REALM:
          baseActions.push(
            { id: 'secret_treasure', name: '秘境夺宝', description: '争夺宝藏，获得机缘', command: 'secret_treasure', icon: '💎', implemented: false },
            { id: 'secret_dungeon', name: '秘境副本', description: '挑战秘境，历练身心', command: 'secret_dungeon', icon: '🏰', implemented: false },
            { id: 'explore', name: '探索秘境', description: '感悟天地法则', command: 'explore', icon: '🏛️', implemented: true }
          );
          break;
        case LOCATION_NAMES.CLOUD_SEA:
          baseActions.push(
            { id: 'cloud_ruins', name: '云海遗址', description: '探索仙人遗迹，寻找传承', command: 'cloud_ruins', icon: '🏛️', implemented: false },
            { id: 'cloud_fishing', name: '云海垂钓', description: '垂钓云中灵鱼，收获机缘', command: 'cloud_fishing', icon: '🎣', implemented: false },
            { id: 'explore', name: '探索云海', description: '淬炼神魂，提升境界', command: 'explore', icon: '☁️', implemented: true }
          );
          break;
        default:
          baseActions.push(
            { id: 'status', name: '查看状态', description: '检查自身状况', command: 'status', icon: '📊', implemented: true },
            { id: 'help', name: '查看帮助', description: '了解可用命令', command: 'help', icon: '❓', implemented: true }
          );
      }

      // 添加通用行动
      baseActions.push(
        { id: 'bag', name: '查看背包', description: '管理物品与资源', command: 'bag', icon: '🎒', implemented: true },
        { id: 'travel', name: '出行游历', description: '前往其他地点', command: 'travel', icon: '🚶', implemented: true }
      );

      return baseActions;
    },

    sceneFeatures() {
      const location = this.gameState.location;
      const features = [];

      switch (location) {
        case LOCATION_NAMES.CAVE:
          features.push(
            { id: 'safe', text: '安全修炼场所', icon: '🛡️' },
            { id: 'herbs', text: '灵田药圃资源', icon: '🌱' },
            { id: 'artifacts', text: '上古传承玉简', icon: '📚' }
          );
          break;
        case LOCATION_NAMES.BACK_MOUNTAIN:
          features.push(
            { id: 'beasts', text: '妖兽灵禽出没', icon: '🐺' },
            { id: 'resources', text: '珍稀矿石材料', icon: '⛏️' },
            { id: 'trials', text: '宗门试炼之地', icon: '⚡' }
          );
          break;
        case LOCATION_NAMES.SECRET_REALM:
          features.push(
            { id: 'time', text: '时间流速特殊', icon: '⏰' },
            { id: 'enlightenment', text: '天地法则感悟', icon: '🌟' },
            { id: 'treasures', text: '仙府秘宝传承', icon: '💎' }
          );
          break;
        case LOCATION_NAMES.CLOUD_SEA:
          features.push(
            { id: 'soul', text: '神魂淬炼之地', icon: '☁️' },
            { id: 'lightning', text: '天劫雷灵汇聚', icon: '⚡' },
            { id: 'immortal', text: '太古仙人遗址', icon: '🏛️' }
          );
          break;
      }

      return features;
    }
  },
  methods: {
    getSceneIcon() {
      const location = this.gameState.location;
      const iconMap = {
        '洞府': '🏛️',
        '后山': '🌲',
        '秘境': '🏛️',
        '云海': '☁️'
      };
      return iconMap[location] || '🏛️';
    },

    executeAction(command) {
      // 检查命令是否已实现
      const implementedCommands = [
        'refine_pill',
        'refine_treasure',
        'explore',
        'bag',
        'travel',
        'status',
        'help',
        'treasure_hall'
      ];

      if (implementedCommands.includes(command)) {
        this.$emit('execute-command', command);
      } else {
        // 未实现的命令输出提示消息到控制台
        this.$emit('show-message', '功能暂未开放，敬请期待！');
      }
    }
  }
};
</script>

<style scoped>
/* CSS Variables for consistent theming */
.scene-info-panel {
  --primary-color: rgba(139, 92, 246, 0.3);
  --secondary-color: rgba(59, 130, 246, 0.08);
  --accent-color: rgba(16, 185, 129, 0.15);
  --warning-color: rgba(251, 191, 36, 0.15);
  --text-primary: #e6eef8;
  --text-secondary: #94a3b8;
  --text-muted: #cbd5e1;
  --border-radius: 8px;
  --border-radius-lg: 12px;
  --shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  --transition: all 0.2s ease;

  background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%);
  border: 2px solid var(--primary-color);
  border-radius: var(--border-radius-lg);
  padding: 12px;
  margin-bottom: 0;
  box-shadow: var(--shadow);
  backdrop-filter: blur(8px);
  position: relative;
  overflow: hidden;
  height: calc(50vh - 24px);
  display: flex;
  flex-direction: column;
}

.scene-info-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 20% 80%, rgba(139, 92, 246, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, var(--secondary-color) 0%, transparent 50%);
  pointer-events: none;
}

/* 场景标题和背景故事一行展示 */
.scene-header-row {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

.scene-title-section {
  flex: 0 0 250px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.scene-background {
  flex: 1;
  min-width: 0;
}

.scene-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 0;
  flex-wrap: wrap;
}

.scene-icon {
  font-size: 18px;
  filter: drop-shadow(0 0 6px rgba(139, 92, 246, 0.4));
}

.scene-title h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  text-shadow: 0 0 8px rgba(139, 92, 246, 0.4);
}

.location-badge {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  padding: 2px 8px;
  border-radius: var(--border-radius);
  font-size: 10px;
  font-weight: 500;
  box-shadow: 0 1px 4px rgba(139, 92, 246, 0.2);
}

.scene-atmosphere {
  display: flex;
  gap: 12px;
}

.atmosphere-indicator,
.time-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-secondary);
  font-size: 11px;
}

.pulse-dot {
  width: 6px;
  height: 6px;
  background: #10b981;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.time-icon {
  font-size: 12px;
}

/* 公共容器样式 */
.panel-section {
  margin-bottom: 12px;
  padding: 10px;
  border-radius: var(--border-radius);
  flex-shrink: 0;
  border: 1px solid transparent;
}

.panel-section:last-child {
  margin-bottom: 0;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.panel-header-icon {
  font-size: 14px;
}

.panel-header-title {
  font-size: 12px;
  font-weight: 600;
}

/* 场景背景故事 */
.scene-background {
  background: rgba(139, 92, 246, 0.08);
  border-color: rgba(139, 92, 246, 0.15);
}

.background-text {
  margin: 0;
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.5;
  text-align: justify;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 可用行动建议 */
.available-actions {
  background: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.15);
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.actions-header .panel-header-title {
  color: #10b981;
}

.action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  overflow-y: auto;
  flex: 1;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition);
  border: 1px solid transparent;
  min-height: 50px;
}

.action-item:hover:not(.action-disabled) {
  background: var(--accent-color);
  border-color: rgba(16, 185, 129, 0.25);
  transform: translateY(-1px);
}

.action-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: rgba(107, 114, 128, 0.1);
  border-color: rgba(107, 114, 128, 0.2);
}

.action-disabled .action-name,
.action-disabled .action-desc {
  color: var(--text-secondary);
}

.action-disabled .coming-soon {
  color: #fbbf24;
}

.action-icon {
  font-size: 16px;
  min-width: 20px;
  flex-shrink: 0;
}

.action-content {
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-text {
  flex: 1;
  min-width: 0;
}

.action-name {
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.action-desc {
  color: var(--text-secondary);
  font-size: 10px;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.coming-soon {
  font-size: 8px;
  font-weight: 500;
  color: #fbbf24;
  flex-shrink: 0;
  margin-left: 6px;
}

/* 场景特色信息 */
.scene-features {
  background: rgba(251, 191, 36, 0.08);
  border-color: rgba(251, 191, 36, 0.15);
}

.features-header .panel-header-title {
  color: #fbbf24;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 4px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  background: rgba(251, 191, 36, 0.08);
  border-radius: 4px;
  border: 1px solid rgba(251, 191, 36, 0.15);
  justify-content: center;
}

.feature-icon {
  font-size: 10px;
  flex-shrink: 0;
}

.feature-text {
  color: var(--text-primary);
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .scene-info-panel {
    padding: 12px;
  }

  .scene-title h3 {
    font-size: 15px;
  }
}

@media (max-width: 900px) {
  .scene-info-panel {
    padding: 10px;
    height: 100%;
  }

  .scene-header-row {
    gap: 10px;
  }

  .scene-title-section {
    flex: 0 0 220px;
  }

  .scene-title-row {
    gap: 5px;
  }

  .scene-icon {
    font-size: 16px;
  }

  .scene-title h3 {
    font-size: 13px;
  }

  .location-badge {
    font-size: 9px;
    padding: 1px 6px;
  }

  .scene-atmosphere {
    gap: 10px;
  }

  .atmosphere-indicator,
  .time-indicator {
    font-size: 10px;
  }

  .scene-background {
    padding: 8px;
  }

  .background-text {
    font-size: 11px;
    line-height: 1.4;
  }

  .available-actions {
    padding: 8px;
  }

  .actions-title {
    font-size: 11px;
  }

  .action-grid {
    gap: 4px;
  }

  .action-item {
    padding: 6px;
    min-height: 45px;
  }

  .action-icon {
    font-size: 14px;
  }

  .action-name {
    font-size: 11px;
  }

  .action-desc {
    font-size: 9px;
  }

  .coming-soon {
    font-size: 7px;
  }

  .scene-features {
    padding: 8px;
  }

  .features-title {
    font-size: 11px;
  }

  .features-grid {
    grid-template-columns: repeat(auto-fit, minmax(75px, 1fr));
    gap: 3px;
  }

  .feature-item {
    padding: 3px 5px;
  }

  .feature-icon {
    font-size: 9px;
  }

  .feature-text {
    font-size: 9px;
  }
}

@media (max-width: 768px) {
  .scene-info-panel {
    padding: 6px;
    height: 100%;
    border-radius: 8px;
  }

  .scene-header-row {
    flex-direction: column;
    gap: 6px;
  }

  .scene-title-section {
    flex: none;
  }

  .scene-title {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 4px;
  }

  .scene-atmosphere {
    gap: 4px;
    align-items: center;
  }

  .scene-icon {
    font-size: 16px;
  }

  .scene-title h3 {
    font-size: 13px;
  }

  .location-badge {
    font-size: 8px;
    padding: 1px 5px;
  }

  .atmosphere-indicator,
  .time-indicator {
    font-size: 9px;
  }

  .scene-background {
    padding: 6px;
  }

  .background-text {
    font-size: 10px;
    line-height: 1.3;
    -webkit-line-clamp: 3;
  }

  .available-actions {
    padding: 6px;
  }

  .actions-title {
    font-size: 10px;
  }

  .action-grid {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .action-item {
    padding: 4px;
    min-height: 35px;
  }

  .action-icon {
    font-size: 12px;
    min-width: 18px;
  }

  .action-name {
    font-size: 10px;
  }

  .action-desc {
    font-size: 8px;
    line-height: 1.1;
    -webkit-line-clamp: 1;
  }

  .coming-soon {
    font-size: 7px;
    margin-left: 4px;
  }

  .action-content {
    min-width: 0;
  }

  .scene-features {
    padding: 6px;
  }

  .features-title {
    font-size: 10px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .feature-item {
    padding: 2px 4px;
    justify-content: flex-start;
  }

  .feature-icon {
    font-size: 8px;
  }

  .feature-text {
    font-size: 8px;
  }
}

@media (max-width: 480px) {
  .scene-info-panel {
    padding: 4px;
    height: 100%;
    border-radius: 6px;
  }

  .scene-header-row {
    gap: 4px;
  }

  .scene-icon {
    font-size: 14px;
  }

  .scene-title h3 {
    font-size: 12px;
  }

  .location-badge {
    font-size: 7px;
    padding: 1px 4px;
  }

  .scene-atmosphere {
    gap: 4px;
  }

  .atmosphere-indicator,
  .time-indicator {
    font-size: 8px;
  }

  .scene-background {
    padding: 4px;
  }

  .background-text {
    font-size: 9px;
    -webkit-line-clamp: 2;
  }

  .available-actions {
    padding: 4px;
  }

  .actions-title {
    font-size: 9px;
  }

  .action-grid {
    gap: 2px;
  }

  .action-item {
    padding: 3px;
    min-height: 30px;
  }

  .action-icon {
    font-size: 11px;
    min-width: 16px;
  }

  .action-name {
    font-size: 11px;
  }

  .action-desc {
    font-size: 9px;
    -webkit-line-clamp: 1;
  }

  .scene-features {
    padding: 4px;
  }

  .features-title {
    font-size: 9px;
  }

  .feature-item {
    padding: 2px 3px;
  }

  .feature-icon {
    font-size: 7px;
  }

  .feature-text {
    font-size: 7px;
  }
}
</style>
